import { useState, useEffect, useContext, useRef } from 'react';
import PageContext from '../sub-components/PageContext';
import HeaderBanner from '../sub-components/HeaderBanner.tsx';
import Footer from '../sub-components/Footer.tsx';
import PageBreadcrumbs from '../sub-components/PageBreadcrumbs.jsx';
import SnackbarAlert from '../sub-components/SnackbarAlert';
import OrderedDataTable from '../sub-components/OrderedDataTableCopy.jsx';
import ExportExcelButton from '../sub-components/ExportExcelButton';
import PageDropdownRedux from '../sub-components/PageDropdownReduxCopy.jsx';
import PageInputReduxTooltip from '../sub-components/PageInputReduxTooltopCopy.jsx';
import ButtonComponent from '../sub-components/ButtonComponent.jsx';
import ClientProfileWorkflowRow from '../sub-components/ClientProfileWorkflowRow.jsx';
import ClientProfileWorkflowAuditModal from '../sub-components/ClientProfileWorkflowAuditModal.jsx';
import ClientProfileWorkflowCommentModal from '../sub-components/ClientProfileWorkflowCommentModal.jsx';
import ClientProfileWorkflowActionOffcanvas from '../sub-components/ClientProfileWorkflowActionOffcanvas.jsx';
import standardSearch from '../functions/standardSearch';
import standardFilter from '../functions/standardFilter';
import applyFilters from '../functions/applyFilters';
import { Typography } from '@mui/material';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import { HttpContext } from '../../api-services/HttpService';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../redux/index';

const baseTextStyle = {
    fontFamily: 'Roboto regular, Helvetica, Arial, sans-serif'
};

const ClientProfileWorkflowPage = () => {
    const { GET } = useContext(HttpContext);

    // redux persistent state — search only (not sharing workflowStatus with RebateSummary)
    const rebateSummarySearch = useSelector((state) => state.rebateSummarySearch);
    const dispatch = useDispatch();
    const { setRebateSummarySearch } = bindActionCreators(actionCreators, dispatch);

    // local status filter state (not shared with Rebate Summary)
    const [workflowStatus, setWorkflowStatus] = useState('All');
    const workflowActionStatusList = ['All', 'Pending', 'Final Review', 'Accepted', 'Rejected'];

    // table state
    const [showDataList, setShowDataList] = useState([]);
    const [dataList, setDataList] = useState([]);
    const [tableHeader, setTableHeader] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [enableReset, setEnableReset] = useState(false);
    const [isMenuLoaded, setIsMenuLoaded] = useState(false);

    // checkbox / selection state
    const [checkAll, setCheckAll] = useState(false);
    const [workflowActionCheckedList, setWorkflowActionCheckedList] = useState([]);
    const [checkBoxChange, setCheckBoxChange] = useState(false);

    // modal state
    const [commentModal, setCommentModal] = useState(false);
    const [auditModal, setAuditModal] = useState(false);
    const [auditRow, setAuditRow] = useState(null);
    const [actionOffcanvas, setActionOffcanvas] = useState(false);
    const [actionType, setActionType] = useState('');

    // snackbar state
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarColor, setSnackbarColor] = useState('warning');

    const initialRender = useRef(true);

    const snackbarAlert = (show, message, color) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarColor(color);
    };

    // reset filters
    const resetToDefault = () => {
        setWorkflowStatus('All');
        setRebateSummarySearch('');
    };

    const enableResetAll = () => {
        return workflowStatus !== 'All' || rebateSummarySearch !== '';
    };

    useEffect(() => {
        if (isMenuLoaded && enableResetAll()) {
            setEnableReset(true);
        } else {
            setEnableReset(false);
        }
    }, [workflowStatus, rebateSummarySearch, isMenuLoaded]);

    // get workflow data from backend
    const [fieldDisplayNameMap, setFieldDisplayNameMap] = useState({});
    const [payoutMethodMap, setPayoutMethodMap] = useState({});
    const [achAccountTypeMap, setAchAccountTypeMap] = useState({});
    
    const getWorkflowData = () => {
        setShowSpinner(true);
        return new Promise((resolve, reject) => {
            GET('/client/clientProfileWorkflow', {}).then(res => {
                setDataList(res.data.WorkflowData);
                setShowDataList(res.data.WorkflowData);
                setTableHeader(res.data.GuiTableCols);
                setFieldDisplayNameMap(res.data.FieldDisplayNameMap || {});
                setPayoutMethodMap(res.data.PayoutMethodMap || {});
                setAchAccountTypeMap(res.data.AchAccountTypeMap || {});
                setIsMenuLoaded(true);
                setShowSpinner(false);
                resolve(res);
            }).catch(err => {
                setShowSpinner(false);
                reject(err);
            });
        });
    };

    const refreshWorkflowData = () => {
        getWorkflowData().catch(err => console.log(err));
    };

    useEffect(() => {
        getWorkflowData().catch(err => console.log(err));
    }, []);

    // filter logic
    useEffect(() => {
        if (!initialRender.current) {
            const functionList = [
                { function: standardSearch, value: rebateSummarySearch, flag: 0, cols: ['mastercompanyname', 'mastercompanyid', 'companyname', 'companyid'] },
                { function: standardFilter, value: workflowStatus, flag: 1, cols: ['status'] }
            ];
            setShowDataList(applyFilters(dataList, functionList));
        } else {
            initialRender.current = false;
        }
    }, [dataList, rebateSummarySearch, workflowStatus]);

    // sync checked list when showDataList changes
    useEffect(() => {
        let tempList = workflowActionCheckedList.filter(row => showDataList.includes(row));
        setWorkflowActionCheckedList(tempList);
    }, [showDataList, checkAll]);

    // check all handler — only check rows matching current status AND where user has permission (checkbox is visible)
    const onCheckboxClick = () => {
        setCheckBoxChange(!checkBoxChange);
        if (!checkAll) {
            setCheckAll(true);
            // only select rows where the checkbox is actually visible (hasRole is true)
            let allowedRows = showDataList.filter(row => {
                let hasRole = false;
                if (workflowStatus === 'Pending' && row['userroleid'] === process.env.REACT_APP_SUBMITTER_ROLE_ID) {
                    hasRole = true;
                } else if (workflowStatus === 'Final Review' && row['userroleid'] === process.env.REACT_APP_REVIEWER_ROLE_ID) {
                    hasRole = true;
                }
                return row['status'] === workflowStatus && hasRole;
            });
            setWorkflowActionCheckedList(allowedRows);
        } else {
            setCheckAll(false);
            setWorkflowActionCheckedList([]);
        }
    };

    // selection status component
    const getCurrentCheckBoxStatusComponent = () => {
        let len = workflowActionCheckedList.length;
        if (len === 0) {
            return (
                <div className="d-flex justify-content-center align-items-center gap-1">
                    <Typography sx={{ whiteSpace: 'nowrap', color: '#004364', fontSize: '14px', fontFamily: 'Roboto medium' }}>
                        Select Companies from below to Review
                    </Typography>
                    <ArrowCircleDownIcon sx={{ fontSize: '24px', color: '#004364' }} />
                </div>
            );
        }
        return (
            <Typography sx={{ whiteSpace: 'nowrap', color: '#001317', fontSize: '14px', fontFamily: 'Roboto medium' }}>
                {len > 1 ? `${len} Companies selected` : '1 Company selected'}
            </Typography>
        );
    };

    const RowComponent = (row, count, tableHeader, stickyEnabled, index, selectableRows) => {
        return (
            <ClientProfileWorkflowRow
                row={row}
                count={count}
                tableHeader={tableHeader}
                stickyEnabled={stickyEnabled}
                rowIndex={index}
                selectableRows={selectableRows}
            />
        );
    };

    const settings = {
        dataList,
        tableHeader,
        checkAll,
        setCheckAll,
        workflowActionCheckedList,
        setWorkflowActionCheckedList,
        checkBoxChange,
        showDataList,
        setShowDataList,
        workflowStatus,
        setWorkflowStatus,
        commentModal,
        setCommentModal,
        auditModal,
        setAuditModal,
        auditRow,
        setAuditRow,
        actionOffcanvas,
        setActionOffcanvas,
        actionType,
        setActionType,
        refreshWorkflowData,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        snackbarColor,
        snackbarAlert,
        fieldDisplayNameMap,
        payoutMethodMap,
        achAccountTypeMap
    };

    return (
        <div className="summary-page" style={baseTextStyle}>
            <PageContext.Provider value={settings}>
                <HeaderBanner />
                <div className="pt-1 ps-3 ms-3">
                    <PageBreadcrumbs />
                </div>
                {/* modals */}
                <ClientProfileWorkflowAuditModal />
                <ClientProfileWorkflowCommentModal />
                <ClientProfileWorkflowActionOffcanvas checkList={workflowActionCheckedList} />
                <SnackbarAlert />

                <div className="container-fluid d-flex flex-column">
                    <div className="d-flex flex-column flex-grow-1 p-2">
                        {/* Filter Panel */}
                        <div className="p-3 mb-3" style={{ borderRadius: '12px', background: '#fff' }}>
                            <div className="d-flex justify-content-between">
                                <div className="d-flex flex-wrap gap-3">
                                    <div className="pe-3">
                                        <PageInputReduxTooltip tag="Company Name | Company ID" tooltip="Search by Company Name or Company ID" value={rebateSummarySearch} setValue={setRebateSummarySearch} width="250px" />
                                    </div>
                                    <div className="pe-3 align-self-center text-decoration-underline" style={{ color: `${enableReset ? '#0295BE' : '#BDBDBD'}`, cursor: 'pointer', fontFamily: 'Roboto medium' }} onClick={() => enableReset ? resetToDefault() : null}>
                                        Reset All
                                    </div>
                                </div>
                                <div className="align-self-start">
                                    <ExportExcelButton tag="client-profile-workflow" dataList={showDataList} tableHeader={tableHeader} />
                                </div>
                            </div>
                        </div>

                        {/* Data Table Panel */}
                        <div className="flex-grow-1" style={{ borderRadius: '12px', background: '#fff' }}>
                            <div className="m-3 d-flex justify-content-between">
                                <PageDropdownRedux isColorCoded={true} tag="Showing Current Status" list={workflowActionStatusList} value={workflowStatus} setValue={setWorkflowStatus} />
                                <div className="d-flex gap-2 align-items-center">
                                    <div className="align-self-center">
                                        {getCurrentCheckBoxStatusComponent()}
                                    </div>
                                    {workflowActionCheckedList.length > 0 && workflowStatus === 'Pending' &&
                                        <ButtonComponent
                                            border={'#fcd800 1px solid'} background={'#fcd800'} color={'#001317'}
                                            tag="Submit for Review"
                                            width="150px"
                                            onButtonClick={() => {
                                                setActionType('Submit for Review');
                                                setActionOffcanvas(true);
                                            }}
                                        />
                                    }
                                    {workflowActionCheckedList.length > 0 && workflowStatus === 'Final Review' &&
                                        <div className="d-flex gap-2">
                                            <ButtonComponent
                                                border={'#fcd800 1px solid'} background={'#fcd800'} color={'#001317'}
                                                tag="Approve"
                                                onButtonClick={() => {
                                                    setActionType('Approve');
                                                    setActionOffcanvas(true);
                                                }}
                                            />
                                            <ButtonComponent
                                                border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'}
                                                tag="Reject"
                                                onButtonClick={() => {
                                                    setActionType('Reject');
                                                    setActionOffcanvas(true);
                                                }}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                            <OrderedDataTable
                                RowComponent={RowComponent}
                                tableHeader={tableHeader}
                                dataList={showDataList}
                                showSpinner={showSpinner}
                                numToPage={50}
                                skeletonHeight="28vh"
                                selectedRowsList={workflowActionCheckedList}
                                sortingEnabled={true}
                                selectAllStatus={checkAll}
                                onSelectAll={onCheckboxClick}
                                stickyFirstColumnEnabled={true}
                                selectableRows
                            />
                        </div>
                    </div>
                </div>
                <Footer cssClassName="footer-main-comp" />
            </PageContext.Provider>
        </div>
    );
};


export default ClientProfileWorkflowPage;
